import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Camera, ExternalLink } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHubspot } from "@fortawesome/free-brands-svg-icons";
import { db } from "../../lib/firebase";
import { useAuth } from "../../context/AuthContext";
import { useFirestoreDocOnce } from "../../hooks/useFirestoreDocOnce";
import { useSetUserPhoto } from "../../hooks/useUserPhotoMutation";
import {
  useDeletePerson,
  useSetUserRole,
  useUpdateUserOnBehalf,
} from "../../hooks/useUserManagement";
import { useSnackbar } from "../../context/SnackbarContext";
import { useUnsavedChangesGuard } from "../../hooks/useUnsavedChangesGuard";
import { TypeToConfirmDialog } from "../../components/TypeToConfirmDialog";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import {
  HubSpotMatchDialog,
  type HubSpotMatchTarget,
} from "../../components/HubSpotMatchDialog";
import {
  HubSpotReconcileDialog,
  type HubSpotReconcileTarget,
} from "../../components/HubSpotReconcileDialog";
import {
  getPersonInternal,
  savePersonInternal,
  unlinkHubSpotContact,
} from "../../services/personInternalService";
import { getHubSpotContact } from "../../services/hubspotService";
import {
  getLibraryHeadshotForContact,
  importSharePointHeadshots,
} from "../../services/headshotService";
import {
  appendHubSpotNotesBlock,
  buildHubSpotContactUrl,
  buildHubSpotNotesBlock,
  buildHubSpotPull,
  isHubSpotConfigured,
  HUBSPOT_ORANGE,
  HUBSPOT_PULL_FIELD_LABELS,
  type HubSpotPullField,
} from "../../utils/hubspot";
import { ProfileTab } from "./tabs/ProfileTab";
import { ContactLogisticsTab } from "./tabs/ContactLogisticsTab";
import { InternalNotesTab } from "./tabs/InternalNotesTab";
import { EventsTab } from "./tabs/EventsTab";
import { PhoneSaveBar } from "../../components/phone/PhoneSaveBar";
import { useIsPhone } from "../../hooks/useIsPhone";
import {
  userEditSchema,
  type UserEditFormData,
} from "../../schemas/userProfileSchema";
import {
  canEditInternalNotes,
  canViewInternalNotes,
} from "./userDetailPermissions";
import type {
  HubSpotContactSuggestion,
  Person,
  PersonInternal,
  PersonProfile,
} from "@event-planner/shared";

const EMPTY_FORM: UserEditFormData = {
  honorificPrefix: null,
  honorificSuffix: null,
  firstName: "",
  lastName: "",
  role: "invitee",
  title: null,
  organization: null,
  bio: null,
  phone: null,
  linkedIn: null,
  website: null,
  workCity: null,
  workState: null,
  dietaryRestrictions: [],
  accessibilityNeeds: null,
  notes: null,
};

function buildDefaultsFromPerson(
  person: Person,
  profile: PersonProfile | null,
  internal: PersonInternal | null,
): UserEditFormData {
  return {
    honorificPrefix: person.honorificPrefix ?? null,
    honorificSuffix: person.honorificSuffix ?? null,
    firstName: person.firstName ?? "",
    lastName: person.lastName ?? "",
    role: person.role ?? "invitee",
    title: profile?.title ?? null,
    organization: profile?.organization ?? null,
    bio: profile?.bio ?? null,
    phone: profile?.phone ?? null,
    linkedIn: profile?.linkedIn ?? null,
    website: profile?.website ?? null,
    workCity: profile?.workCity ?? null,
    workState: profile?.workState ?? null,
    dietaryRestrictions: profile?.dietaryRestrictions ?? [],
    accessibilityNeeds: profile?.accessibilityNeeds ?? null,
    notes: internal?.notes ?? null,
  };
}

export function UserDetailPage() {
  const isPhone = useIsPhone();
  // Router param renamed `personId` per ADR-136. The legacy `userId`
  // route is gone; the `/users/contact/:contactId` route was dropped.
  const { personId } = useParams<{ personId?: string }>();

  const docRef = useMemo(
    () => (personId ? doc(db, "persons", personId) : null),
    [personId],
  );

  const { data: person, loading, refetch } = useFirestoreDocOnce<Person>(
    docRef,
  );

  const { user: callerUser, role: callerRole } = useAuth();
  const isCallerAdmin = callerRole === "admin";
  // Self-edit check uses the authAccounts pointer in the long run; under
  // the Phase 1 coupling (personId == uid for claimed users) callerUser.uid
  // still equals personId for the caller's own row.
  const isSelfView = callerUser?.uid === personId;

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tabIndex, setTabIndex] = useState(0);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [sourcesLoading, setSourcesLoading] = useState(true);

  // ── HubSpot link state (ADR-172 review-first) ─────────────────────
  // `hubspotContactId` is the SAVED link; `hubspotStash` is a pending
  // link picked in the match dialog — it commits (with the reviewed
  // form values) only when the organizer saves.
  const [hubspotContactId, setHubspotContactId] = useState<string | null>(null);
  const [hubspotStash, setHubspotStash] = useState<{
    contactId: string;
    repcrd: string | null;
  } | null>(null);
  /** Field labels the last pull prefilled — drives the review banner. */
  const [hubspotPrefilled, setHubspotPrefilled] = useState<string[]>([]);
  /**
   * Library photo staged for the PENDING link (ADR-181 link-time pull).
   * Tagged with the contactId it belongs to: it only renders/commits
   * while the stash still points at that contact, so a cleared or
   * re-picked link can never import a stale photo.
   */
  const [pendingHeadshot, setPendingHeadshot] = useState<{
    contactId: string;
    driveItemId: string;
    fileName: string;
    thumbnailUrl: string | null;
  } | null>(null);
  /** ContactId whose library lookup is still in flight — drives the spinner. */
  const [headshotLookupFor, setHeadshotLookupFor] = useState<string | null>(
    null,
  );
  const [matchTarget, setMatchTarget] = useState<HubSpotMatchTarget | null>(
    null,
  );
  const [reconcileTarget, setReconcileTarget] =
    useState<HubSpotReconcileTarget | null>(null);
  const [unlinkConfirmOpen, setUnlinkConfirmOpen] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { upload: uploadPhoto, loading: uploadingPhoto } = useSetUserPhoto();
  const { remove: deletePersonAction, loading: deleting } = useDeletePerson();
  const { update: updateUser, loading: updatingUser } = useUpdateUserOnBehalf();
  const { changeRole, loading: changingRole } = useSetUserRole();
  const { showSuccess, showError, showWarning } = useSnackbar();

  // The staged photo is only live while the pending link it belongs to
  // still is — clearing or replacing the stash orphans it harmlessly.
  const stagedHeadshot =
    pendingHeadshot && hubspotStash?.contactId === pendingHeadshot.contactId
      ? pendingHeadshot
      : null;
  const headshotLookupPending =
    !!hubspotStash && headshotLookupFor === hubspotStash.contactId;

  // ── Single page-level form (mirrors EventForm pattern) ──────────────────
  const form = useForm<UserEditFormData>({
    resolver: zodResolver(userEditSchema),
    defaultValues: EMPTY_FORM,
  });

  // Track the person's currently-saved role so we know whether to call the
  // dedicated `setUserRole` callable on submit.
  const [savedRole, setSavedRole] = useState<UserEditFormData["role"]>(
    "invitee",
  );

  // Load the supporting Firestore docs (personProfile + personInternal)
  // and reset the form once everything is in.
  useEffect(() => {
    if (!person || !personId) return;
    let cancelled = false;
    setSourcesLoading(true);

    Promise.all([
      getDoc(doc(db, "personProfiles", personId))
        .then((snap) =>
          snap.exists()
            ? ({ id: snap.id, ...snap.data() } as PersonProfile)
            : null,
        )
        .catch(() => null),
      getPersonInternal(personId).catch(() => null),
    ]).then(([profile, internal]) => {
      if (cancelled) return;
      form.reset(buildDefaultsFromPerson(person, profile, internal));
      setSavedRole(person.role ?? "invitee");
      setHubspotContactId(internal?.hubspotContactId ?? null);
      setSourcesLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [person, personId, form]);

  /**
   * Routes a picked HubSpot contact into the FORM for review (ADR-172):
   * empty fields fill (marked dirty), differing fields go to the
   * reconcile dialog, the "From HubSpot" facts block appends to the
   * notes field, and the link pointer is stashed. Nothing persists here
   * — the organizer's Save commits it all.
   */
  const applyHubSpotContact = useCallback(
    (suggestion: HubSpotContactSuggestion) => {
      if (!person) return;
      // A pick already pending? Revert to the last-saved baseline first
      // so the previous contact's fills and notes block can't linger
      // under the new link. (Manual edits made before that pick revert
      // too — the banner told the organizer these values were under
      // review.)
      if (hubspotStash) {
        form.reset();
        setHubspotPrefilled([]);
        setPendingHeadshot(null);
      }
      const values = form.getValues();
      const { fills, conflicts } = buildHubSpotPull(suggestion, values);
      const applied: string[] = [];
      for (const [field, value] of Object.entries(fills)) {
        form.setValue(field as HubSpotPullField, value, {
          shouldDirty: true,
        });
        applied.push(HUBSPOT_PULL_FIELD_LABELS[field as HubSpotPullField]);
      }
      if (canEditInternalNotes(callerRole)) {
        const block = buildHubSpotNotesBlock(suggestion);
        if (block) {
          const { notes, appended } = appendHubSpotNotesBlock(
            values.notes,
            block,
          );
          if (appended) {
            form.setValue("notes", notes, { shouldDirty: true });
            applied.push("Internal notes");
          }
        }
      }
      setHubspotStash({
        contactId: suggestion.contactId,
        repcrd: suggestion.repcrd ?? null,
      });
      setHubspotPrefilled(applied);
      // Library lookup (ADR-181): a photo, if found, joins the review as a
      // staged avatar; a spinner runs while the check is in flight. Any
      // failure (library unconfigured, Graph hiccup) degrades to "no
      // photo" — it never blocks the link.
      setPendingHeadshot(null);
      setHeadshotLookupFor(suggestion.contactId);
      getLibraryHeadshotForContact({ contactId: suggestion.contactId })
        .then((response) => {
          if (response.headshot) {
            setPendingHeadshot({
              contactId: suggestion.contactId,
              ...response.headshot,
            });
          }
        })
        .catch(() => {})
        .finally(() => {
          setHeadshotLookupFor((prev) =>
            prev === suggestion.contactId ? null : prev,
          );
        });
      if (conflicts.length > 0) {
        setReconcileTarget({
          displayName: person.displayName || person.email || "this person",
          conflicts,
        });
      }
    },
    [form, person, callerRole, hubspotStash],
  );

  // List/report pages hand a picked contact over via ?hubspotContact=
  // (they have no form to review in). Apply once the form has loaded,
  // then strip the param so a refresh doesn't re-apply.
  const hubspotContactParam = searchParams.get("hubspotContact");
  useEffect(() => {
    if (!hubspotContactParam || sourcesLoading || !person) return;
    let cancelled = false;
    getHubSpotContact({ contactId: hubspotContactParam })
      .then((response) => {
        if (!cancelled) applyHubSpotContact(response.contact);
      })
      .catch((err) => {
        if (!cancelled) {
          showError(
            err instanceof Error
              ? err.message
              : "Could not load the HubSpot contact.",
          );
        }
      })
      .finally(() => {
        if (cancelled) return;
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete("hubspotContact");
            return next;
          },
          { replace: true },
        );
      });
    return () => {
      cancelled = true;
    };
  }, [
    hubspotContactParam,
    sourcesLoading,
    person,
    applyHubSpotContact,
    setSearchParams,
    showError,
  ]);

  const submitting =
    updatingUser || changingRole || form.formState.isSubmitting;
  const { dialog: unsavedGuardDialog } = useUnsavedChangesGuard(
    form.formState.isDirty || !!hubspotStash,
    submitting,
  );

  async function onSubmit(values: UserEditFormData) {
    setServerError(null);
    if (!personId) return;
    try {
      // Role lives behind a dedicated callable (Auth claim + fan-out to the
      // person's existing eventParticipants rows via setUserRole). Admin
      // access to events comes from the claim alone (ADR-191). Apply
      // first, only if changed.
      if (isCallerAdmin && values.role !== savedRole) {
        await changeRole(personId, values.role);
      }
      await updateUser({
        userId: personId,
        eventId: "__global__",
        userFields: {
          firstName: values.firstName,
          lastName: values.lastName,
          honorificPrefix: values.honorificPrefix || null,
          honorificSuffix: values.honorificSuffix || null,
        },
        profileFields: {
          title: values.title || null,
          organization: values.organization || null,
          bio: values.bio || null,
          phone: values.phone || null,
          linkedIn: values.linkedIn || null,
          website: values.website || null,
          workCity: values.workCity || null,
          workState: values.workState || null,
          dietaryRestrictions: values.dietaryRestrictions ?? [],
          accessibilityNeeds: values.accessibilityNeeds || null,
        },
      });
      if (canEditInternalNotes(callerRole)) {
        await savePersonInternal(personId, {
          notes: values.notes || null,
          // A pending HubSpot link (ADR-172) commits with the reviewed
          // form values — this Save is the single commit point.
          ...(hubspotStash
            ? {
                hubspotContactId: hubspotStash.contactId,
                hubspotRepcrd: hubspotStash.repcrd,
              }
            : {}),
        });
      }
      if (hubspotStash) {
        setHubspotContactId(hubspotStash.contactId);
        setHubspotStash(null);
        setHubspotPrefilled([]);
      }
      // Staged library photo commits AFTER the link (ADR-181): the import
      // callable re-verifies by HubSpotID against personInternals, which
      // the savePersonInternal above just wrote. Failure never fails the
      // Save — the person keeps their reviewed data either way.
      let photoImported = false;
      if (stagedHeadshot) {
        setPendingHeadshot(null);
        try {
          const importResponse = await importSharePointHeadshots({
            items: [{ driveItemId: stagedHeadshot.driveItemId, personId }],
          });
          const outcome = importResponse.results[0];
          if (outcome?.status === "imported") {
            photoImported = true;
          } else {
            showWarning(
              `Saved, but the library photo wasn't imported: ${outcome?.message ?? outcome?.status ?? "unknown result"}`,
            );
          }
        } catch (err) {
          showWarning(
            `Saved, but the library photo import failed: ${err instanceof Error ? err.message : "unknown error"}`,
          );
        }
      }
      // Reset baseline so isDirty goes false; the data refetch below
      // will re-reset with the canonical server-side values.
      form.reset(values);
      setSavedRole(values.role);
      showSuccess(photoImported ? "Saved — library photo imported" : "Saved");
      refetch();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save";
      setServerError(message);
      showError(message);
    }
  }

  async function handleDeleteConfirm() {
    if (!person) return;
    const label = person.displayName || person.email || "Person";
    try {
      await deletePersonAction(person);
      showSuccess(`${label} deleted.`);
      setDeleteOpen(false);
      navigate("/users");
    } catch (err) {
      showError(
        err instanceof Error ? err.message : "Failed to delete person.",
      );
    }
  }

  async function handleUnlinkConfirm() {
    if (!personId) return;
    setUnlinking(true);
    try {
      await unlinkHubSpotContact(personId);
      showSuccess("HubSpot link removed. Previously pulled data is kept.");
      setUnlinkConfirmOpen(false);
      setHubspotContactId(null);
    } catch (err) {
      showError(
        err instanceof Error ? err.message : "Failed to unlink from HubSpot.",
      );
    } finally {
      setUnlinking(false);
    }
  }

  async function handlePhotoSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !personId) return;
    e.target.value = "";
    try {
      await uploadPhoto("__global__", personId, file);
      showSuccess("Photo updated");
      refetch();
    } catch (err) {
      showError(
        err instanceof Error ? err.message : "Failed to upload photo",
      );
    }
  }

  if (loading || sourcesLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!person) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h5" component="h1">
          Person not found
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          This record may have been deleted.
        </Typography>
      </Box>
    );
  }

  const displayName = person.displayName || person.email || "Unknown";
  const role = person.role;
  const photoURL = person.photoURL;
  const email = person.email;
  const showHubSpot = isHubSpotConfigured() && !!email;
  const isClaimed = person.authUid !== null;
  const statusLabel = isClaimed ? "Active" : "Pending";
  const statusColor = isClaimed ? "success" : "warning";
  const showInternalNotes = canViewInternalNotes(callerRole);
  const canWriteInternalNotes = canEditInternalNotes(callerRole);
  const eventsTabIndex = showInternalNotes ? 3 : 2;

  // The link-status block rendered inside the Internal Notes tab — the
  // link pointer lives in personInternals, so that tab is its home
  // (owner call from the ADR-172 click-through). It is part of the
  // REVIEW surface: it renders as soon as a pick is pending, so the
  // reviewer can open the actual CRM record they'd be linking before
  // anything saves. Unlinking a saved link stays a confirmed,
  // immediate client write; removing a pending pick just reverts the
  // form.
  const reviewContactId = hubspotStash?.contactId ?? hubspotContactId;
  const hubspotLinkSection =
    showHubSpot && reviewContactId ? (
      <Box sx={{ mb: 3 }} data-testid="hubspot-link-section">
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          HubSpot
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
          <FontAwesomeIcon
            icon={faHubspot}
            style={{ fontSize: 16, color: HUBSPOT_ORANGE }}
          />
          <Typography variant="body2">
            {hubspotStash
              ? "Will be linked to this HubSpot contact when you save"
              : "Linked to a HubSpot contact"}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
        >
          <Button
            variant="outlined"
            endIcon={<ExternalLink size={16} />}
            href={
              buildHubSpotContactUrl({
                email,
                contactId: reviewContactId,
              }) ?? ""
            }
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: HUBSPOT_ORANGE,
              borderColor: HUBSPOT_ORANGE,
              "&:hover": { borderColor: HUBSPOT_ORANGE },
            }}
            data-testid="hubspot-open-record-button"
          >
            Open in HubSpot
          </Button>
          {hubspotStash ? (
            <Button
              variant="outlined"
              color="warning"
              onClick={() => {
                form.reset();
                setHubspotStash(null);
                setHubspotPrefilled([]);
                setPendingHeadshot(null);
                setHeadshotLookupFor(null);
              }}
              data-testid="hubspot-remove-pending-button"
            >
              Remove pending link
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="warning"
              disabled={unlinking}
              onClick={() => setUnlinkConfirmOpen(true)}
              data-testid="hubspot-unlink-button"
            >
              Unlink
            </Button>
          )}
        </Stack>
      </Box>
    ) : null;

  return (
    <FormProvider {...form}>
      {unsavedGuardDialog}
      <Box
        component="form"
        onSubmit={form.handleSubmit(onSubmit)}
        noValidate
      >
        {/* Header. Phone: wraps, so the chips and buttons drop to a row of
            their own under the photo and name. */}
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          useFlexGap={isPhone}
          flexWrap={isPhone ? "wrap" : undefined}
          sx={{ mb: 3 }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            hidden
            onChange={handlePhotoSelected}
          />
          <Tooltip title="Change photo">
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              badgeContent={
                <IconButton
                  size="small"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  sx={{
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    width: 24,
                    height: 24,
                    "&:hover": { bgcolor: "primary.dark" },
                  }}
                >
                  {uploadingPhoto ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <Camera size={14} />
                  )}
                </IconButton>
              }
            >
              <Avatar
                src={photoURL ?? undefined}
                alt={displayName}
                sx={{
                  width: 56,
                  height: 56,
                  cursor: "pointer",
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                {displayName[0] || "?"}
              </Avatar>
            </Badge>
          </Tooltip>
          {headshotLookupPending && (
            <Stack
              alignItems="center"
              spacing={0.5}
              data-testid="pending-library-headshot-loading"
            >
              <CircularProgress size={24} />
              <Typography variant="caption" color="text.secondary">
                Checking the Headshot Library…
              </Typography>
            </Stack>
          )}
          {stagedHeadshot && (
            <Stack
              alignItems="center"
              spacing={0.5}
              data-testid="pending-library-headshot"
            >
              <Tooltip
                title={
                  photoURL
                    ? "The Headshot Library has a photo for the linked contact — it replaces the current one when you save"
                    : "From the Headshot Library — imports when you save"
                }
              >
                <Avatar
                  src={stagedHeadshot.thumbnailUrl ?? undefined}
                  alt="Staged library photo"
                  sx={{
                    width: 56,
                    height: 56,
                    border: "2px dashed",
                    borderColor: "info.main",
                  }}
                />
              </Tooltip>
              <Chip
                size="small"
                color="info"
                variant="outlined"
                label="Photo from Headshot Library"
              />
            </Stack>
          )}
          <Box sx={isPhone ? { flex: 1, minWidth: 0 } : { flex: 1 }}>
            <Typography
              variant="h4"
              component="h1"
              sx={isPhone ? { overflowWrap: "anywhere" } : undefined}
            >
              {displayName}
            </Typography>
            {email && (
              <Typography variant="body2" color="text.secondary">
                {email}
              </Typography>
            )}
          </Box>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            useFlexGap={isPhone}
            flexWrap={isPhone ? "wrap" : undefined}
            sx={isPhone ? { width: "100%" } : undefined}
          >
            {role && (
              <Chip
                label={role}
                size="small"
                sx={{ textTransform: "capitalize" }}
              />
            )}
            <Chip
              label={statusLabel}
              size="small"
              color={statusColor as "success" | "warning"}
              variant="outlined"
            />
            {showHubSpot &&
              (hubspotContactId ? (
                <Tooltip title={`Open ${displayName} in HubSpot`}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={
                      <FontAwesomeIcon
                        icon={faHubspot}
                        style={{ fontSize: 16 }}
                      />
                    }
                    href={
                      buildHubSpotContactUrl({
                        email,
                        contactId: hubspotContactId,
                      }) ?? ""
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: HUBSPOT_ORANGE,
                      borderColor: HUBSPOT_ORANGE,
                      "&:hover": { borderColor: HUBSPOT_ORANGE },
                    }}
                    aria-label={`Open ${displayName} in HubSpot (linked)`}
                    data-testid="open-hubspot-contact-button"
                  >
                    HubSpot
                  </Button>
                </Tooltip>
              ) : (
                <Tooltip title={`Not linked — find ${displayName} in HubSpot`}>
                  <Button
                    variant="outlined"
                    size="small"
                    color="inherit"
                    startIcon={
                      <FontAwesomeIcon
                        icon={faHubspot}
                        style={{ fontSize: 16 }}
                      />
                    }
                    onClick={() =>
                      setMatchTarget({
                        personId: person.id,
                        email,
                        displayName,
                      })
                    }
                    aria-label={`Not linked — find ${displayName} in HubSpot`}
                    data-testid="open-hubspot-contact-button"
                  >
                    HubSpot
                  </Button>
                </Tooltip>
              ))}
            {isCallerAdmin && (
              <Tooltip
                title={
                  isSelfView
                    ? "Use your mobile Settings screen to delete your own account."
                    : ""
                }
              >
                <span>
                  <Button
                    color="error"
                    variant="outlined"
                    size="small"
                    disabled={isSelfView || deleting}
                    onClick={() => setDeleteOpen(true)}
                  >
                    Delete Person
                  </Button>
                </span>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        <TypeToConfirmDialog
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete person"
          message={`Permanently delete ${person.displayName || person.email || "this person"} and every piece of their personal data across every event they belong to? This cascades through profile, participations, RSVPs, notes, check-ins, feedback, survey responses, notification receipts, speaker links, and (if claimed) the Firebase Auth record. This cannot be undone.`}
          confirmText={person.displayName || person.email || ""}
          confirmLabel="Delete Person"
          loading={deleting}
        />

        {serverError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {serverError}
          </Alert>
        )}

        {hubspotStash && (
          <Alert
            severity="info"
            sx={{ mb: 3 }}
            data-testid="hubspot-prefill-banner"
          >
            {hubspotPrefilled.length > 0
              ? `From HubSpot: ${hubspotPrefilled.join(", ")}. Review the ` +
                `values below — nothing is saved (the HubSpot link` +
                `${stagedHeadshot ? ", the library photo," : ""} included) ` +
                `until you click Save Changes.`
              : headshotLookupPending
                ? "HubSpot link ready — checking the Headshot Library for " +
                  "a photo. Nothing is saved until you click Save Changes."
                : stagedHeadshot
                  ? "HubSpot link ready, and the Headshot Library has a " +
                    "photo for this contact (staged above). Click Save " +
                    "Changes to apply both."
                  : "HubSpot link ready — click Save Changes to confirm " +
                    "it. No profile fields needed filling."}
          </Alert>
        )}

        {/* Tabs row with inline Save button (mirrors EventForm) */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Tabs
            value={tabIndex}
            onChange={(_, v) => setTabIndex(v)}
            variant={isPhone ? "scrollable" : "standard"}
            sx={
              isPhone
                ? { flex: 1, minWidth: 0, borderBottom: 1, borderColor: "divider" }
                : { flex: 1, borderBottom: 1, borderColor: "divider" }
            }
          >
            <Tab label="Profile" />
            <Tab label="Contact & Logistics" />
            {showInternalNotes && <Tab label="Internal Notes" />}
            <Tab label="Events" />
          </Tabs>
          {/* Phone: Save moves to the sticky bar at the bottom. */}
          {tabIndex !== eventsTabIndex && !isPhone && (
            <Button
              type="submit"
              variant="contained"
              disabled={
                submitting || (!form.formState.isDirty && !hubspotStash)
              }
              startIcon={
                submitting ? <CircularProgress size={18} /> : undefined
              }
              sx={{ ml: 2, flexShrink: 0 }}
            >
              {submitting ? "Saving..." : "Save Changes"}
            </Button>
          )}
        </Box>

        {tabIndex === 0 && <ProfileTab />}
        {tabIndex === 1 && <ContactLogisticsTab />}
        {showInternalNotes && tabIndex === 2 && (
          <InternalNotesTab
            editable={canWriteInternalNotes}
            hubspotSection={hubspotLinkSection}
          />
        )}
        {tabIndex === eventsTabIndex && (
          <EventsTab personId={person.id} personEmail={person.email} />
        )}

        {isPhone && tabIndex !== eventsTabIndex && (
          <PhoneSaveBar>
            <Button
              type="submit"
              variant="contained"
              disabled={
                submitting || (!form.formState.isDirty && !hubspotStash)
              }
              startIcon={
                submitting ? <CircularProgress size={18} /> : undefined
              }
            >
              {submitting ? "Saving..." : "Save Changes"}
            </Button>
          </PhoneSaveBar>
        )}

        <HubSpotMatchDialog
          target={matchTarget}
          onClose={() => setMatchTarget(null)}
          onConfirm={(suggestion) => {
            setMatchTarget(null);
            applyHubSpotContact(suggestion);
          }}
        />

        <HubSpotReconcileDialog
          target={reconcileTarget}
          onClose={() => setReconcileTarget(null)}
          onApply={(takeFields) => {
            if (!reconcileTarget) return;
            const takenLabels: string[] = [];
            for (const field of takeFields) {
              const conflict = reconcileTarget.conflicts.find(
                (c) => c.field === field,
              );
              if (!conflict) continue;
              form.setValue(field, conflict.theirs, { shouldDirty: true });
              takenLabels.push(HUBSPOT_PULL_FIELD_LABELS[field]);
            }
            if (takenLabels.length > 0) {
              setHubspotPrefilled((prev) => [
                ...prev,
                ...takenLabels.filter((l) => !prev.includes(l)),
              ]);
            }
          }}
        />

        <ConfirmDialog
          open={unlinkConfirmOpen}
          onClose={() => setUnlinkConfirmOpen(false)}
          onConfirm={handleUnlinkConfirm}
          title="Unlink from HubSpot"
          message={`Remove the HubSpot link for ${displayName}? Profile data already pulled from the CRM stays; only the connection is removed. You can re-link at any time.`}
          confirmLabel="Unlink"
          confirmColor="warning"
          loading={unlinking}
        />
      </Box>
    </FormProvider>
  );
}
